import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, RotateCcw, Plus, Clock, Hash, FileText, Save, Edit3, ChevronRight } from 'lucide-react';

const isDevelopment = process.env.NODE_ENV === 'development';
const API_BASE_URL = isDevelopment ? 'http://localhost:7777/api' : '/api';

interface PromptType {
  prompt_type: string;
  canonical_prompt_type?: string;
  display_name?: string;
  version_number: number | null;
  is_active: number;
  content_size: number;
  created_at?: string;
  created_by?: string;
  change_summary?: string;
  id?: string;
  description?: string;
}

interface PromptVersion {
  id: string;
  prompt_type: string;
  version_number: number;
  created_at: string;
  created_by: string;
  is_active: number;
  change_summary: string | null;
  content_hash: string;
  content_size: number;
  content?: string;
}

const TYPE_LABELS: Record<string, string> = {
  metadata_generation: 'Metadata Generation',
  show_grammar: 'Show Grammar',
  agent_system: 'Agent System Prompt',
  transcript_refinement: 'Transcript Refinement',
  metadata_validation_rules: 'Metadata Validation Rules',
  description_compression: 'Description Compression',
  narrative_generation: 'Narrative Generation',
  agent_narrative_generation: 'Agent: Topic Map Generation',
  description_generation: 'Description Generation',
  agent_transcript_refinement: 'Agent: Transcript Refinement',
  agent_description_generation: 'Agent: Description Generation',
  speaker_inference: 'Speaker Inference',
  title_generation: 'Title Generation',
  chat_system: 'Chat System Prompt',
  llm_api_docs: 'CLI and MCP Docs',
  humanizer: 'Humanizer',
  diarization_enrichment: 'Diarization Enrichment',
};

interface PromptManagerProps {
  isOpen: boolean;
  onClose: () => void;
  token: string;
  isDarkMode: boolean;
}

const PromptManager: React.FC<PromptManagerProps> = ({ isOpen, onClose, token, isDarkMode }) => {
  const [types, setTypes] = useState<PromptType[]>([]);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [versions, setVersions] = useState<PromptVersion[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [activeContent, setActiveContent] = useState<string>('');
  const [editContent, setEditContent] = useState<string>('');
  const [changeSummary, setChangeSummary] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [viewingVersion, setViewingVersion] = useState<PromptVersion | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  }), [token]);

  // Load types on open
  useEffect(() => {
    if (!isOpen) return;
    fetch(`${API_BASE_URL}/v1/prompts/types`, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => {
        const t = data.types || [];
        setTypes(t);
        if (t.length > 0 && !selectedType) setSelectedType(t[0].prompt_type);
      })
      .catch(() => setError('Failed to load prompt types'));
  }, [isOpen, authHeaders, selectedType]);

  // Load versions + active content when type selected
  useEffect(() => {
    if (!selectedType) return;
    setLoadingVersions(true);
    setVersions([]);
    setActiveContent('');
    setIsEditing(false);
    setViewingVersion(null);
    setError(null);
    setSuccessMessage(null);

    Promise.all([
      fetch(`${API_BASE_URL}/v1/prompts/${selectedType}/versions`, { headers: authHeaders() }).then(r => r.json()),
      fetch(`${API_BASE_URL}/v1/prompts/${selectedType}`, { headers: authHeaders() }).then(r => r.json()),
    ]).then(([versionsData, contentData]) => {
      setVersions(versionsData.versions || []);
      setActiveContent(contentData.content || '');
      setEditContent(contentData.content || '');
    }).catch(() => {
      setError('Failed to load versions');
    }).finally(() => {
      setLoadingVersions(false);
    });
  }, [selectedType, authHeaders]);

  const refreshSelected = () => {
    const t = selectedType;
    setSelectedType(null);
    setTimeout(() => setSelectedType(t), 50);
  };

  const handleSave = async () => {
    if (!selectedType || !editContent.trim()) return;
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const res = await fetch(`${API_BASE_URL}/v1/prompts/${selectedType}/versions`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ content: editContent, change_summary: changeSummary || undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');

      if (data.deduplicated) {
        setSuccessMessage('Content unchanged — no new version created');
      } else {
        setSuccessMessage(`Version ${data.version_number} created`);
      }
      setIsEditing(false);
      setChangeSummary('');
      refreshSelected();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleActivate = async (versionId: string) => {
    if (!selectedType) return;
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/v1/prompts/${selectedType}/versions/${versionId}/activate`, {
        method: 'PUT',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Failed to activate version');
      setSuccessMessage('Version activated');
      refreshSelected();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleViewVersion = async (version: PromptVersion) => {
    if (viewingVersion?.id === version.id) {
      setViewingVersion(null);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/v1/prompts/${selectedType}/versions/${version.id}`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      setViewingVersion(data);
    } catch {
      setError('Failed to load version content');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 's' && (e.metaKey || e.ctrlKey) && isEditing) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      if (isEditing) {
        setIsEditing(false);
        setEditContent(activeContent);
        setChangeSummary('');
      } else {
        onClose();
      }
    }
  };

  // Content to display: viewing a specific version or the active content
  const displayContent = viewingVersion ? (viewingVersion.content || '') : activeContent;
  const displayLabel = viewingVersion
    ? `v${viewingVersion.version_number} — ${viewingVersion.change_summary || 'No summary'}`
    : 'Active Content';
  const selectedTypeMeta = types.find(t => t.prompt_type === selectedType);
  const selectedTypeLabel = selectedTypeMeta?.display_name || (selectedType ? TYPE_LABELS[selectedType] || selectedType : '');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col" onKeyDown={handleKeyDown}>
      {/* Full-screen backdrop */}
      <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-950' : 'bg-gray-50'}`} />

      {/* Header bar */}
      <div className={`relative z-10 flex items-center justify-between px-4 py-2 border-b shrink-0 ${
        isDarkMode ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-200 text-gray-900'
      }`}>
        <div className="flex items-center gap-3">
          <FileText size={18} />
          <h2 className="text-sm font-semibold">Prompt Manager</h2>
          {selectedType && (
            <>
              <ChevronRight size={14} className="text-gray-400" />
              <span className="text-sm font-medium">{selectedTypeLabel}</span>
              {isEditing && (
                <span className="text-xs px-2 py-0.5 rounded bg-brand-yellow/15 text-brand-yellow font-medium">EDITING</span>
              )}
              {viewingVersion && !isEditing && (
                <span className="text-xs px-2 py-0.5 rounded bg-brand-yellow/15 text-brand-yellow font-medium">
                  Viewing v{viewingVersion.version_number}
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Messages inline in header */}
          {error && (
            <span className="text-xs px-2 py-1 rounded bg-red-600/20 text-red-400">
              {error}
              <button onClick={() => setError(null)} className="ml-1 underline">dismiss</button>
            </span>
          )}
          {successMessage && (
            <span className="text-xs px-2 py-1 rounded bg-brand-yellow/15 text-brand-yellow">
              {successMessage}
              <button onClick={() => setSuccessMessage(null)} className="ml-1 underline">dismiss</button>
            </span>
          )}
          <button onClick={onClose} className={`p-1.5 rounded transition-colors ${
            isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-600'
          }`}>
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Main content: sidebar + editor */}
      <div className="relative z-10 flex flex-1 min-h-0">
        {/* Left sidebar: types + versions */}
        <div className={`w-64 shrink-0 flex flex-col border-r overflow-hidden ${
          isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          {/* Prompt types */}
          <div className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider border-b ${
            isDarkMode ? 'text-gray-500 border-gray-700' : 'text-gray-400 border-gray-200'
          }`}>
            Prompt Types
          </div>
          <div className="flex-none overflow-y-auto">
            {types.map(t => (
              <button
                key={t.prompt_type}
                onClick={() => { setSelectedType(t.prompt_type); setViewingVersion(null); }}
                className={`w-full text-left px-3 py-2.5 text-sm flex items-center justify-between transition-colors ${
                  selectedType === t.prompt_type
                    ? isDarkMode ? 'bg-gray-800 text-white border-l-2 border-brand-yellow' : 'bg-[#fff6e5] text-brand-navy border-l-2 border-brand-yellow'
                    : isDarkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="truncate">{t.display_name || TYPE_LABELS[t.prompt_type] || t.prompt_type}</span>
                <span className={`text-xs shrink-0 ml-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  v{t.version_number || 1}
                </span>
              </button>
            ))}
          </div>

          {/* Version history for selected type */}
          {selectedType && (
            <>
              <div className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider border-t border-b flex items-center justify-between ${
                isDarkMode ? 'text-gray-500 border-gray-700' : 'text-gray-400 border-gray-200'
              }`}>
                <span className="flex items-center gap-1"><Clock size={12} /> Versions</span>
                <span>{versions.length}</span>
              </div>
              <div className="flex-1 overflow-y-auto min-h-0">
                {loadingVersions ? (
                  <p className="px-3 py-4 text-xs text-gray-500">Loading...</p>
                ) : (
                  versions.map(v => (
                    <div
                      key={v.id}
                      className={`px-3 py-2 text-xs border-b cursor-pointer transition-colors ${
                        v.is_active
                          ? isDarkMode ? 'bg-brand-yellow/10 border-brand-yellow/30' : 'bg-[#fff6e5] border-[#f0d79a]'
                          : viewingVersion?.id === v.id
                            ? isDarkMode ? 'bg-brand-navy/20 border-gray-700' : 'bg-[#e8edf2] border-gray-200'
                            : isDarkMode ? 'border-gray-800 hover:bg-gray-800' : 'border-gray-100 hover:bg-gray-50'
                      }`}
                      onClick={() => handleViewVersion(v)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <Hash size={10} className="text-gray-400" />
                          <span className="font-mono font-medium">v{v.version_number}</span>
                          {v.is_active && (
                            <span className={`px-1 py-0.5 rounded text-[10px] font-medium ${
                              isDarkMode ? 'bg-brand-yellow/15 text-brand-yellow' : 'bg-[#fff3d6] text-[#7a560c]'
                            }`}>active</span>
                          )}
                        </div>
                        <span className="text-gray-500">{(v.content_size / 1024).toFixed(1)}KB</span>
                      </div>
                      <div className={`mt-1 flex items-center gap-1.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        <span>{v.created_by}</span>
                        <span>{new Date(v.created_at).toLocaleDateString()}</span>
                      </div>
                      {v.change_summary && (
                        <div className={`mt-0.5 italic truncate ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {v.change_summary}
                        </div>
                      )}
                      {!v.is_active && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleActivate(v.id); }}
                          className="mt-1.5 px-2 py-0.5 rounded text-[10px] bg-brand-yellow text-brand-navy hover:bg-[#ffc54d] flex items-center gap-1 w-fit"
                        >
                          <RotateCcw size={9} /> Revert to this version
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        {/* Main content area */}
        <div className={`flex-1 flex flex-col min-h-0 ${isDarkMode ? 'bg-gray-950' : 'bg-gray-50'}`}>
          {!selectedType ? (
            <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
              Select a prompt type from the sidebar
            </div>
          ) : loadingVersions ? (
            <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
              Loading...
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className={`flex items-center justify-between px-4 py-2 border-b shrink-0 ${
                isDarkMode ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200'
              }`}>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{displayLabel}</span>
                  <span className="text-gray-400">({displayContent.length.toLocaleString()} chars)</span>
                </div>
                <div className="flex items-center gap-2">
                  {isEditing ? (
                    <>
                      <input
                        value={changeSummary}
                        onChange={e => setChangeSummary(e.target.value)}
                        placeholder="Change summary..."
                        className={`text-xs px-2 py-1 rounded border w-56 ${
                          isDarkMode ? 'border-gray-600 bg-gray-800 text-gray-200' : 'border-gray-300 bg-white text-gray-900'
                        }`}
                        onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
                      />
                      <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="text-xs px-3 py-1 rounded bg-brand-yellow text-brand-navy hover:bg-[#ffc54d] disabled:opacity-50 flex items-center gap-1"
                      >
                        <Save size={12} /> {isSaving ? 'Saving...' : 'Save Version'}
                      </button>
                      <button
                        onClick={() => { setIsEditing(false); setEditContent(activeContent); setChangeSummary(''); }}
                        className={`text-xs px-3 py-1 rounded ${
                          isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        Cancel
                      </button>
                      <span className="text-[10px] text-gray-500">Ctrl+S to save</span>
                    </>
                  ) : (
                    <>
                      {viewingVersion && (
                        <button
                          onClick={() => setViewingVersion(null)}
                          className={`text-xs px-3 py-1 rounded ${
                            isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          Back to Active
                        </button>
                      )}
                      <button
                        onClick={() => { setIsEditing(true); setEditContent(viewingVersion ? (viewingVersion.content || '') : activeContent); setViewingVersion(null); }}
                        className="text-xs px-3 py-1 rounded bg-brand-navy text-brand-cloud hover:bg-[#1a2230] flex items-center gap-1"
                      >
                        <Edit3 size={12} /> Edit
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Type description (if available from API) */}
              {selectedType && types.find(t => t.prompt_type === selectedType)?.description && (
                <div className={`px-4 py-2 text-xs border-b ${
                  isDarkMode ? 'bg-gray-900/30 border-gray-800 text-gray-400' : 'bg-[#fff8eb] border-gray-200 text-gray-500'
                }`}>
                  {types.find(t => t.prompt_type === selectedType)?.description}
                </div>
              )}

              {/* Content area — full remaining height */}
              <div className="flex-1 min-h-0 overflow-hidden">
                {isEditing ? (
                  <textarea
                    ref={textareaRef}
                    value={editContent}
                    onChange={e => setEditContent(e.target.value)}
                    className={`w-full h-full text-sm font-mono p-4 resize-none focus:outline-none ${
                      isDarkMode ? 'bg-gray-950 text-gray-200 caret-white' : 'bg-white text-gray-900'
                    }`}
                    spellCheck={false}
                    autoFocus
                  />
                ) : (
                  <pre className={`w-full h-full text-sm font-mono p-4 overflow-auto whitespace-pre-wrap break-words ${
                    isDarkMode ? 'bg-gray-950 text-gray-300' : 'bg-white text-gray-800'
                  }`}>
                    {displayContent}
                  </pre>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromptManager;
